package app.footyguide.web.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import kotlinx.coroutines.delay
import app.footyguide.web.theme.FootyTheme
import app.footyguide.core.locale.LocalStrings

/**
 * Animated banner for selecting heart team.
 * Rotates team logos and background colors with a professional fade effect.
 */
@Composable
fun HeartTeamBanner(
    selectedTeamId: String?,
    onClick: () -> Unit
) {
    if (selectedTeamId != null) return
    val strings = LocalStrings.current

    val storageBase = "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/teams/"
    val storageSuffix = ".webp"
    
    // Looping through a subset for the banner
    val teamIds = listOf(
        "flamengo", "palmeiras", "saopaulo", "corinthians", 
        "vasco", "fluminense", "botafogo", "atleticomg", 
        "cruzeiro", "gremio", "internacional", "bahia"
    )

    var currentIndex by remember { mutableStateOf(0) }
    var opacity by remember { mutableStateOf(1.0) }
    
    val currentTeamId = teamIds[currentIndex]
    
    // We can't use fromId anymore as it's purely dynamic. 
    // For the loop banner, we use a simple color mapper since we don't have the full Team objects yet
    val currentPrimary = remember(currentTeamId) {
        when(currentTeamId) {
            "flamengo", "internacional", "athleticopr" -> "#E53935"
            "palmeiras", "juventude" -> "#006437"
            "saopaulo" -> "#FF0000"
            "bahia", "cruzeiro", "fortaleza", "remo" -> "#004B8F"
            "vasco", "botafogo", "corinthians", "atleticomg" -> "#FFFFFF"
            else -> "#0B6E4F"
        }
    }

    LaunchedEffect(Unit) {
        while (true) {
            delay(2500)
            opacity = 0.0
            delay(500)
            currentIndex = (currentIndex + 1) % teamIds.size
            opacity = 1.0
            delay(500)
        }
    }

    Div({
        id("banner-choose-team")
        classes("fg-heart-team-banner")
        onClick { onClick() }
        style {
            margin(8.px, 16.px, 20.px)
            
            // DYNAMIC BACKGROUND COLOR based on the looping team
            val rgb = hexToRgb(currentPrimary)
            backgroundColor(Color("rgba($rgb, 0.1)"))
            
            borderRadius(14.px)
            padding(14.px, 16.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
            gap(16.px)
            border(1.px, LineStyle.Solid, Color("var(--border)"))
            property("box-shadow", "0 2px 4px rgba(22, 24, 29, 0.04)")
            
            cursor("pointer")
            property("transition", "background-color 1s ease")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            Div({
                style {
                    width(40.px)
                    height(40.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    flexShrink(0)
                }
            }) {
                Img(src = "$storageBase$currentTeamId$storageSuffix") {
                    style {
                        width(CrestSizes.HERO_CARD_PX.px)
                        height(CrestSizes.HERO_CARD_PX.px)
                        property("width", CrestSizes.HERO_CARD_CSS)
                        property("height", CrestSizes.HERO_CARD_CSS)
                        property("object-fit", "contain")
                        opacity(opacity)
                        property("transition", "opacity 0.5s ease-in-out")
                    }
                }
            }

            Div {
                Div({
                    style {
                        fontWeight(700)
                        fontSize(14.px)
                        fontFamily("Sora, sans-serif")
                        color(Color("var(--fg)"))
                        letterSpacing((-0.2).px)
                    }
                }) {
                    Text(strings.chooseYourClub)
                }
                Div({
                    style {
                        fontSize(12.px)
                        fontFamily("Inter, sans-serif")
                        fontWeight(500)
                        color(Color("var(--muted)"))
                    }
                }) {
                    Text(strings.favoritesSubtitle)
                }
            }
        }

        Span({
            style {
                color(Color(currentPrimary))
                fontWeight(900)
                property("transition", "color 1s ease")
            }
        }) {
            LucideIcon(Icons.CHEVRON_RIGHT, size = 20, color = currentPrimary)
        }
    }
}

private fun hexToRgb(hex: String): String {
    return try {
        val h = hex.removePrefix("#")
        val r = h.substring(0, 2).toInt(16)
        val g = h.substring(2, 4).toInt(16)
        val b = h.substring(4, 6).toInt(16)
        "$r, $g, $b"
    } catch (e: Exception) {
        "34, 197, 94"
    }
}
